// Piano-roll editor for a MIDI clip. Canvas grid: pitch (rows) × time (cols).
// Add notes (click empty), move (drag body), resize (drag right edge), delete
// (right-click / Delete). Edits commit to the store on mouse-up (one undo step)
// and re-grow the clip's duration to cover its notes. Notes audition on edit.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el } from "../util";
import { openModal } from "./modal";
import { MidiNote } from "../state/model";
import { GM_DRUM_LABELS } from "../audio/drums";

const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const isBlack = (m: number) => [1, 3, 6, 8, 10].includes(((m % 12) + 12) % 12);
const noteName = (m: number) => NAMES[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);

const GUTTER = 42;   // left key-label column
const TOPPAD = 4;
const ROW_H = 13;    // px per semitone
const BEAT_PX = 60;  // px per beat (horizontal)

export function openPianoRoll(store: Store, engine: AudioEngine, clipId: string) {
  const found = store.findClip(clipId);
  if (!found || !found.clip.notes) return;
  const clip0 = found.clip; // stable ref (store mutates it in place on updateClip)
  const trackId = found.track.id;
  const color = found.track.color;
  const isDrums = found.track.instrument?.type === "drums";
  const gutter = isDrums ? 82 : GUTTER; // wider label column for drum names
  const secPerBeat = 60 / store.project.tempo;
  const pps = BEAT_PX / secPerBeat; // px per second

  let notes: MidiNote[] = JSON.parse(JSON.stringify(found.clip.notes));
  let selected = -1;
  let snapDiv = 4; // 1/4 note grid default (0 = off)
  let lastLen = secPerBeat / 2; // default new-note length (eighth)
  let playhead: number | null = null; // preview playback position (sec), null = stopped
  let playRaf = 0, playStart = 0;

  // pitch range: fit to notes (min ~2 octaves), or the GM drum range for a kit
  let lo = isDrums ? 35 : 60, hi = isDrums ? 59 : 72;
  if (notes.length) { lo = Math.min(...notes.map((n) => n.midi)); hi = Math.max(...notes.map((n) => n.midi)); }
  lo -= 2; hi += 2;
  if (!isDrums && hi - lo < 24) { const c = Math.round((lo + hi) / 2); lo = c - 12; hi = c + 12; }
  lo = Math.max(0, lo); hi = Math.min(127, hi);
  const rows = hi - lo + 1;

  const gridSec = () => Math.max(found.clip.duration, ...notes.map((n) => n.start + n.dur), secPerBeat * 4) + secPerBeat * 4;

  const canvas = document.createElement("canvas");
  const g = canvas.getContext("2d")!;
  const sizeCanvas = () => { canvas.width = gutter + Math.ceil(gridSec() * pps); canvas.height = TOPPAD + rows * ROW_H; };
  sizeCanvas();

  const yOf = (m: number) => TOPPAD + (hi - m) * ROW_H;
  const midiAtY = (y: number) => hi - Math.floor((y - TOPPAD) / ROW_H);
  const xOf = (t: number) => gutter + t * pps;
  const timeAtX = (x: number) => Math.max(0, (x - gutter) / pps);
  const snap = (t: number) => snapDiv ? Math.round(t / (secPerBeat / snapDiv)) * (secPerBeat / snapDiv) : t;

  function draw() {
    g.clearRect(0, 0, canvas.width, canvas.height);
    // row backgrounds + key labels
    for (let m = lo; m <= hi; m++) {
      const y = yOf(m);
      g.fillStyle = isBlack(m) && !isDrums ? "#11151d" : "#171c26";
      g.fillRect(gutter, y, canvas.width - gutter, ROW_H);
      g.fillStyle = isBlack(m) && !isDrums ? "#0b0d12" : "#222a38";
      g.fillRect(0, y, gutter, ROW_H);
      if (isDrums || m % 12 === 0 || ROW_H >= 12) { g.fillStyle = "#8a95a6"; g.font = "9px Segoe UI"; g.fillText(isDrums ? (GM_DRUM_LABELS[m] ?? String(m)) : noteName(m), 4, y + ROW_H - 3); }
      g.strokeStyle = "#0b0d12"; g.beginPath(); g.moveTo(gutter, y + ROW_H + 0.5); g.lineTo(canvas.width, y + ROW_H + 0.5); g.stroke();
    }
    // vertical beat/bar grid
    const bpb = store.project.beatsPerBar;
    const totalBeats = Math.ceil(gridSec() / secPerBeat);
    for (let b = 0; b <= totalBeats; b++) {
      const x = xOf(b * secPerBeat);
      g.strokeStyle = b % bpb === 0 ? "#39455c" : "#222a38";
      g.lineWidth = 1; g.beginPath(); g.moveTo(x + 0.5, 0); g.lineTo(x + 0.5, canvas.height); g.stroke();
      // subdivisions
      if (snapDiv > 1) for (let s = 1; s < snapDiv; s++) { const xs = xOf((b + s / snapDiv) * secPerBeat); g.strokeStyle = "#1a2230"; g.beginPath(); g.moveTo(xs + 0.5, 0); g.lineTo(xs + 0.5, canvas.height); g.stroke(); }
    }
    // notes
    for (let i = 0; i < notes.length; i++) {
      const n = notes[i];
      const x = xOf(n.start), w = Math.max(3, n.dur * pps), y = yOf(n.midi);
      g.fillStyle = color; g.globalAlpha = 0.45 + 0.5 * Math.max(0, Math.min(1, n.vel));
      g.fillRect(x, y + 1, w, ROW_H - 2);
      g.globalAlpha = 1;
      g.strokeStyle = i === selected ? "#fff" : "#0b0d12"; g.lineWidth = i === selected ? 2 : 1;
      g.strokeRect(x + 0.5, y + 1.5, w - 1, ROW_H - 3);
    }
    // trim window: dim anything outside [offset, offset+duration] — those notes
    // are cropped out of the clip and won't play (drag the clip edges to reveal).
    const wx0 = xOf(clip0.offset), wx1 = xOf(clip0.offset + clip0.duration);
    g.fillStyle = "rgba(6,8,12,0.55)";
    if (wx0 > gutter) g.fillRect(gutter, 0, wx0 - gutter, canvas.height);
    if (wx1 < canvas.width) g.fillRect(wx1, 0, canvas.width - wx1, canvas.height);
    g.strokeStyle = "#48507a"; g.lineWidth = 1;
    g.beginPath(); g.moveTo(wx0 + 0.5, 0); g.lineTo(wx0 + 0.5, canvas.height); g.moveTo(wx1 + 0.5, 0); g.lineTo(wx1 + 0.5, canvas.height); g.stroke();
    // playhead while previewing
    if (playhead != null) { const px = xOf(playhead); g.strokeStyle = "#5b8cff"; g.lineWidth = 2; g.beginPath(); g.moveTo(px + 0.5, 0); g.lineTo(px + 0.5, canvas.height); g.stroke(); }
  }

  const hitNote = (x: number, y: number) => {
    for (let i = notes.length - 1; i >= 0; i--) {
      const n = notes[i]; const nx = xOf(n.start), nw = Math.max(3, n.dur * pps), ny = yOf(n.midi);
      if (x >= nx && x <= nx + nw && y >= ny && y <= ny + ROW_H) return { i, edge: x >= nx + nw - 6 };
    }
    return null;
  };

  const audition = (midi: number, vel: number) => {
    const inst = engine.instrument(trackId); if (!inst) return;
    engine.resume(); inst.noteOn(midi, vel); setTimeout(() => inst.noteOff(midi), 220);
  };

  let drag: { mode: "move" | "resize"; i: number; grabT: number; origStart: number; origMidi: number; origDur: number } | null = null;

  canvas.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    const h = hitNote((e as MouseEvent).offsetX, (e as MouseEvent).offsetY);
    if (h) { notes.splice(h.i, 1); selected = -1; commit(); }
  });

  canvas.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return;
    const x = e.offsetX, y = e.offsetY;
    if (x < gutter) { audition(midiAtY(y), 0.8); return; } // click the key column = preview pitch
    const h = hitNote(x, y);
    if (h) {
      selected = h.i; const n = notes[h.i];
      drag = { mode: h.edge ? "resize" : "move", i: h.i, grabT: timeAtX(x), origStart: n.start, origMidi: n.midi, origDur: n.dur };
      if (!h.edge) audition(n.midi, n.vel);
    } else {
      // add a note at the clicked cell
      const start = Math.max(0, snap(timeAtX(x)));
      const midi = Math.max(0, Math.min(127, midiAtY(y)));
      notes.push({ midi, start, dur: lastLen, vel: 0.8 });
      selected = notes.length - 1;
      drag = { mode: "resize", i: selected, grabT: timeAtX(x), origStart: start, origMidi: midi, origDur: lastLen };
      audition(midi, 0.8);
    }
    draw();
  });

  const onMove = (e: MouseEvent) => {
    if (!drag) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const n = notes[drag.i]; if (!n) return;
    const dt = timeAtX(x) - drag.grabT;
    if (drag.mode === "move") {
      n.start = Math.max(0, snap(drag.origStart + dt));
      n.midi = Math.max(0, Math.min(127, midiAtY(y)));
    } else {
      const minLen = snapDiv ? secPerBeat / snapDiv : 0.05;
      n.dur = Math.max(minLen, snap(Math.max(0.05, drag.origDur + dt)) || minLen);
      lastLen = n.dur;
    }
    draw();
  };
  const onUp = () => { if (drag) { drag = null; commit(); } };
  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);

  const onKey = (e: KeyboardEvent) => {
    if ((e.key === "Delete" || e.key === "Backspace") && selected >= 0) {
      e.preventDefault(); notes.splice(selected, 1); selected = -1; commit();
    }
  };
  window.addEventListener("keydown", onKey);

  function commit() {
    notes.sort((a, b) => a.start - b.start);
    // Preserve the clip's trim window; only grow the right edge to swallow notes
    // drawn past it (never auto-shrink, so a timeline trim survives an edit).
    const contentEnd = Math.max(0.5, ...notes.map((n) => n.start + n.dur));
    const dur = Math.max(clip0.duration, contentEnd - clip0.offset);
    store.updateClip(clipId, { notes: JSON.parse(JSON.stringify(notes)), duration: dur });
    sizeCanvas(); draw();
  }

  // ---- preview playback (through the track instrument, at project tempo) ----
  const actx = engine.ctx;
  const clipEnd = () => Math.max(0.5, ...notes.map((n) => n.start + n.dur));
  function stopPlay() {
    if (!playhead && !playRaf) return;
    cancelAnimationFrame(playRaf); playRaf = 0; playhead = null;
    engine.instrument(trackId)?.allNotesOff();
    playBtn.textContent = "▶ Play"; draw();
  }
  function play() {
    if (playhead != null) { stopPlay(); return; }
    engine.resume();
    const inst = engine.instrument(trackId);
    if (!inst) return;
    inst.allNotesOff();
    const lead = 0.08;
    playStart = actx.currentTime + lead;
    for (const n of notes) inst.scheduleNote(n.midi, n.vel, playStart + n.start, n.dur);
    const end = clipEnd();
    playBtn.textContent = "⏹ Stop";
    const loop = () => {
      playhead = actx.currentTime - playStart;
      draw();
      // keep the playhead visible
      const px = xOf(playhead), sl = wrap.scrollLeft, vw = wrap.clientWidth;
      if (px > sl + vw - 40 || px < sl) wrap.scrollLeft = Math.max(0, px - vw * 0.4);
      if (playhead > end + 0.4) { stopPlay(); return; }
      playRaf = requestAnimationFrame(loop);
    };
    playRaf = requestAnimationFrame(loop);
  }

  // ---- UI ----
  const playBtn = el("button", { class: "active", text: "▶ Play" });
  playBtn.addEventListener("click", play);

  const snapSel = el("select") as HTMLSelectElement;
  for (const d of [1, 2, 4, 8, 16, 0]) {
    const o = el("option", { text: d === 0 ? "Snap: Off" : `Snap: 1/${d}`, attrs: { value: String(d) } }) as HTMLOptionElement;
    if (d === snapDiv) o.selected = true;
    snapSel.append(o);
  }
  snapSel.addEventListener("change", () => { snapDiv = Number(snapSel.value); draw(); });

  const wrap = el("div", { class: "pr-wrap" }, [canvas]);
  const bar = el("div", { class: "pr-bar" }, [
    playBtn, snapSel,
    el("span", { class: "hint", text: "Click empty = add · drag = move · drag right edge = length · right-click / Del = delete" }),
  ]);
  const body = el("div", {}, [bar, wrap]);
  draw();

  const close = openModal(`Piano roll — ${found.clip.name}`, body, [{ label: "Done", primary: true, onClick: () => {} }]);
  // tear down global listeners when the modal closes
  const mo = new MutationObserver(() => {
    if (!document.body.contains(wrap)) {
      stopPlay();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("keydown", onKey);
      mo.disconnect();
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  void close;
}
